import { serializePathAndArgs } from "./udf_path_utils.js";
/**
 * The implementation of `OptimisticLocalStore`.
 *
 * This class provides the interface for optimistic updates to modify query results.
 */
class OptimisticLocalStoreImpl {
    constructor(queryResults) {
        this.queryResults = queryResults;
        this.modifiedQueries = [];
    }
    getQuery(name, args) {
        const query = this.queryResults.get(serializePathAndArgs(name, args));
        if (query === undefined) {
            return undefined;
        }
        return query.value;
    }
    setQuery(name, args, value) {
        const queryToken = serializePathAndArgs(name, args);
        const query = {
            udfPath: name,
            args,
            value,
        };
        this.queryResults.set(queryToken, query);
        this.modifiedQueries.push(queryToken);
    }
}
/**
 * A view of all of our query results with optimistic updates applied on top.
 */
export class OptimisticQueryResults {
    constructor() {
        this.queryResults = new Map();
        this.optimisticUpdates = [];
    }
    ingestQueryResultsFromServer(serverQueryResults, optimisticUpdatesToDrop) {
        this.optimisticUpdates = this.optimisticUpdates.filter((updateAndId) => {
            return !optimisticUpdatesToDrop.has(updateAndId.mutationId);
        });
        const oldQueryResults = this.queryResults;
        this.queryResults = new Map(serverQueryResults);
        const localStore = new OptimisticLocalStoreImpl(this.queryResults);
        for (const updateAndId of this.optimisticUpdates) {
            updateAndId.update(localStore);
        }
        // To find the changed queries, just do a shallow comparison
        // TODO(CX-733): Change this so we avoid unnecessary rerenders
        const changedQueries = new Map();
        for (const [queryToken, query] of this.queryResults) {
            const oldQuery = oldQueryResults.get(queryToken);
            if (oldQuery === undefined || oldQuery.value !== query.value) {
                changedQueries.set(queryToken, query.value);
            }
        }
        return changedQueries;
    }
    applyOptimisticUpdate(update, mutationId) {
        // Apply the update to our store
        this.optimisticUpdates.push({
            update,
            mutationId,
        });
        const localStore = new OptimisticLocalStoreImpl(this.queryResults);
        update(localStore);
        // Notify about any query results that changed
        // TODO(CX-733): Change this so we avoid unnecessary rerenders
        const changedQueries = new Map();
        for (const queryToken of localStore.modifiedQueries) {
            const query = this.queryResults.get(queryToken);
            changedQueries.set(queryToken, query.value);
        }
        return changedQueries;
    }
}
//# sourceMappingURL=optimistic_query_set.js.map